import React from 'react'
import { Box, Typography, Chip, CircularProgress, LinearProgress, Avatar } from '@mui/material'
import { CardSeeker, TableSeeker, IconSeeker } from '@ui-seeker/core'
import {
    tableData,
    isLoadingTable,
    userDetailOpen,
    selectedUser
} from './@signals'
import ExportButton from './ExportButton'

// Column definitions
const userColumn = () => ({
    name: 'user_email',
    label: 'User',
    options: {
        customBodyRenderLite: (dataIndex) => {
            const row = tableData.value[dataIndex]
            const isSystem = row?.is_system

            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            bgcolor: isSystem ? 'grey.400' : 'primary.main',
                            fontSize: '0.875rem'
                        }}
                    >
                        {isSystem ? (
                            <IconSeeker icon="robot" size="sm" />
                        ) : (
                            row?.user_email?.charAt(0)?.toUpperCase() || '?'
                        )}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" fontWeight="medium">
                            {row?.user_email || 'Unknown'}
                        </Typography>
                        {isSystem && (
                            <Typography variant="caption" color="text.secondary">
                                Automated
                            </Typography>
                        )}
                    </Box>
                </Box>
            )
        }
    }
})

const totalColumn = () => ({
    name: 'total_actions',
    label: 'Total',
    options: {
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
        customBodyRenderLite: (dataIndex) => {
            const row = tableData.value[dataIndex]
            return (
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight="bold">
                        {row?.total_actions?.toLocaleString() || 0}
                    </Typography>
                </Box>
            )
        }
    }
})

const mapColumn = () => ({
    name: 'map_count',
    label: 'MAP',
    options: {
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
        customBodyRenderLite: (dataIndex) => {
            const row = tableData.value[dataIndex]
            return (
                <Box sx={{ textAlign: 'center' }}>
                    <Chip
                        label={row?.map_count?.toLocaleString() || 0}
                        size="small"
                        color="success"
                        variant="outlined"
                    />
                </Box>
            )
        }
    }
})

const unmapColumn = () => ({
    name: 'unmap_count',
    label: 'UNMAP',
    options: {
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
        customBodyRenderLite: (dataIndex) => {
            const row = tableData.value[dataIndex]
            return (
                <Box sx={{ textAlign: 'center' }}>
                    <Chip
                        label={row?.unmap_count?.toLocaleString() || 0}
                        size="small"
                        color="error"
                        variant="outlined"
                    />
                </Box>
            )
        }
    }
})

const hoursColumn = () => ({
    name: 'hours_worked',
    label: 'Hours',
    options: {
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
        customBodyRenderLite: (dataIndex) => {
            const row = tableData.value[dataIndex]
            return (
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2">
                        {row?.hours_worked || 0}h
                    </Typography>
                </Box>
            )
        }
    }
})

const rateColumn = () => ({
    name: 'rate_per_hour',
    label: 'Rate/Hour',
    options: {
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
        customBodyRenderLite: (dataIndex) => {
            const row = tableData.value[dataIndex]
            const rate = row?.rate_per_hour || 0
            // Max rate for normalization (cap at 100)
            const maxRate = 100
            const normalizedRate = Math.min((rate / maxRate) * 100, 100)

            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, minWidth: 120 }}>
                    <LinearProgress
                        variant="determinate"
                        value={normalizedRate}
                        sx={{
                            flex: 1,
                            maxWidth: 80,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: rate > 50 ? 'success.main' : rate > 25 ? 'warning.main' : 'error.main'
                            }
                        }}
                    />
                    <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 40 }}>
                        {rate.toFixed(1)}
                    </Typography>
                </Box>
            )
        }
    }
})

export default function UserStatsTable() {
    const columns = [
        userColumn(),
        totalColumn(),
        mapColumn(),
        unmapColumn(),
        hoursColumn(),
        rateColumn()
    ]

    const handleRowClick = (rowData, rowMeta) => {
        const user = tableData.value[rowMeta.dataIndex]
        selectedUser.value = user
        userDetailOpen.value = true
    }

    return (
        <CardSeeker>
            <Box sx={{ p: 2 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6">
                            User Performance
                        </Typography>
                        <Chip
                            label={`${tableData.value.length} users`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    </Box>
                    <ExportButton />
                </Box>

                {/* Loading indicator */}
                {isLoadingTable.value && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Table */}
                {!isLoadingTable.value && (
                    <TableSeeker
                        data={tableData.value}
                        columns={columns}
                        options={{
                            selectableRows: 'none',
                            filterType: 'dropdown',
                            responsive: 'standard',
                            rowsPerPage: 25,
                            rowsPerPageOptions: [10, 25, 50],
                            onRowClick: handleRowClick,
                            sortOrder: {
                                name: 'total_actions',
                                direction: 'desc'
                            },
                            textLabels: {
                                body: {
                                    noMatch: 'No user data found'
                                }
                            },
                            setRowProps: () => ({
                                style: { cursor: 'pointer' }
                            })
                        }}
                    />
                )}
            </Box>
        </CardSeeker>
    )
}
